import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Check, X, Download, Trash2, Printer } from 'lucide-react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';


const statusMap = { pending: { label: 'En attente', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' }, confirmed: { label: 'Confirmee', color: 'bg-[#5B6B3F]/10 text-[#5B6B3F]' }, modified: { label: 'Modifiee', color: 'bg-[#1E2D3D]/10 text-[#1E2D3D]' }, rejected: { label: 'Refusee', color: 'bg-[#8C3A3A]/10 text-[#8C3A3A]' } };

export default function ReservationsAdminPage() {
  const [reservations, setReservations] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [historyActionLoading, setHistoryActionLoading] = useState(false);

  const load = () => { api.get(`/reservations${filter ? `?status=${filter}` : ''}`).then(r => setReservations(r.data)).catch(() => {}); };
  useEffect(load, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/reservations/${id}`, { status, admin_note: note });
      if (['confirmed', 'rejected'].includes(status)) {
        window.dispatchEvent(new CustomEvent('admin-request-resolved', {
          detail: { type: 'reservation', referenceKey: 'reservation_id', referenceId: id },
        }));
      }
      toast.success(`Reservation ${status === 'confirmed' ? 'confirmee' : status === 'rejected' ? 'refusee' : 'modifiee'}`);
      setSelected(null); setNote(''); load();
    } catch { toast.error('Erreur'); }
  };

  const downloadBlob = (data, filename, contentType) => {
    const blob = new Blob([data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadAndDeleteHistory = async () => {
    if (!window.confirm("L'historique des réservations sera d'abord téléchargé puis supprimé. Continuer ?")) {
      return;
    }
    setHistoryActionLoading(true);
    try {
      const exportResponse = await api.get('/reservations/history/export', { responseType: 'blob' });
      const disposition = exportResponse.headers['content-disposition'] || '';
      const fileNameMatch = disposition.match(/filename="?([^"]+)"?/);
      const fileName = fileNameMatch?.[1] || 'historique-reservations.xlsx';
      downloadBlob(exportResponse.data, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

      const deleteResponse = await api.delete('/reservations/history');
      toast.success(`Historique téléchargé puis supprimé (${deleteResponse.data?.deleted_count || 0} réservation(s))`);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors du téléchargement ou de la suppression de l'historique");
    } finally {
      setHistoryActionLoading(false);
    }
  };

  const printReservation = async (reservation) => {
    try {
      const response = await api.get(`/reservations/${reservation.id}/print.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!printWindow) throw new Error('popup');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Impossible d'ouvrir la reservation a imprimer.");
    }
  };

  return (
    <div data-testid="admin-reservations-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-2xl font-bold text-[#2A2A28]">Reservations</h1>
        <Button
          type="button"
          variant="outline"
          disabled={historyActionLoading}
          onClick={downloadAndDeleteHistory}
          className="rounded-xl border-[#8C3A3A]/20 text-[#8C3A3A] hover:bg-[#8C3A3A]/5"
          data-testid="reservations-download-delete-history"
        >
          {historyActionLoading ? <Download className="mr-2 h-4 w-4 animate-pulse" /> : <Trash2 className="mr-2 h-4 w-4" />}
          {historyActionLoading ? 'Traitement...' : "Télécharger puis supprimer l'historique"}
        </Button>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        {['', 'pending', 'confirmed', 'rejected'].map(s => (
          <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm"
            className={`rounded-full ${filter === s ? 'bg-[#5B6B3F] text-white' : 'border-[#EBE8E0] text-[#7A7A75]'}`}
            onClick={() => setFilter(s)} data-testid={`filter-${s || 'all'}`}>
            {s === '' ? 'Toutes' : statusMap[s]?.label}
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        {reservations.map(res => (
          <div key={res.id} className="bg-white rounded-xl border border-[#EBE8E0] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`reservation-${res.id}`}>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-[#2A2A28]">{res.name}</h3>
                <Badge className={`text-xs ${statusMap[res.status]?.color || ''}`}>{statusMap[res.status]?.label}</Badge>
              </div>
              <p className="text-xs text-[#5B6B3F] font-medium">Ref: {res.id}</p>
              <p className="text-sm text-[#7A7A75]">{res.date} a {res.time} - {res.guests} personnes</p>
              {res.phone && <p className="text-xs text-[#7A7A75]">Tel: {res.phone}</p>}
              {res.comment && <p className="text-xs text-[#7A7A75] italic mt-1">"{res.comment}"</p>}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" className="rounded-lg border-[#1E2D3D]/20 text-[#1E2D3D]" onClick={() => printReservation(res)} data-testid={`print-reservation-${res.id}`}>
                <Printer className="w-3.5 h-3.5 mr-1" /> Imprimer
              </Button>
              {res.status === 'pending' && (
                <>
                  <Button size="sm" className="rounded-lg bg-[#5B6B3F] hover:bg-[#4A5832] text-white" onClick={() => updateStatus(res.id, 'confirmed')} data-testid={`confirm-${res.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Confirmer
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-lg border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={() => { setSelected(res); setNote(''); }} data-testid={`reject-btn-${res.id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Refuser
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
        {reservations.length === 0 && <p className="text-center text-[#7A7A75] py-8">Aucune reservation</p>}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Refuser la reservation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#7A7A75]">Reservation de {selected?.name} le {selected?.date} a {selected?.time}</p>
            <div><Label className="text-sm">Motif (optionnel)</Label><Textarea className="mt-1 rounded-xl border-[#EBE8E0]" value={note} onChange={e => setNote(e.target.value)} placeholder="Raison du refus..." /></div>
            <Button onClick={() => updateStatus(selected?.id, 'rejected')} className="w-full rounded-xl bg-[#8C3A3A] hover:bg-[#7a3232] text-white" data-testid="confirm-reject">Confirmer le refus</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
